/**
 * The extraction field editor's model — Story 20.2.
 *
 * Recipes for `edit()` and the pure questions the card asks, kept out of the
 * component for the reason `editor/rubric.ts` gives: a recipe mutates an Immer
 * draft and returns nothing, so every change is a patch that undo covers, and
 * all of it is testable without a store or a browser.
 *
 * THE CONTRACT OWNS EVERY RULE HERE. `acceptsExtraction` is `crossReference`'s
 * own question (`packages/schema/src/funnel.ts`), `buildField` ends by handing its
 * result to `extractionFieldSchema`, and the add itself is the vocabulary's
 * `add_extraction_field` (Story 27.5), which holds the rubric's cap — so the card
 * cannot add a field the next save refuses. What this module adds is the WORDING
 * of each refusal, which the schema's messages, written for the API, do not have.
 */
import {
  acceptsExtraction,
  applyOperation,
  extractionFieldSchema,
  type Block,
  type Condition,
  type ExtractionField,
  type Funnel,
} from '@leadgible/schema'
import { COMPOSED, PROMPTED, type ExtractionType } from './extraction-copy'

/** `extractionFieldSchema`'s own bounds, restated where the inputs can use them. */
export const NAME_MAX = 200
export const DESCRIPTION_MAX = 2000
const KEY_MAX = 64

/**
 * The questions a field may read: long-text, and already in `ai_extraction` mode.
 *
 * LONG-TEXT ONLY, where `acceptsExtraction` would also take a short-text
 * question in that mode. The helper promises "long-text questions", and the
 * Build tab's mode control (Story 20.7) appears on long-text questions alone,
 * so narrowing loses no case the dashboard can reach and keeps the sentence
 * true. Everything offered is a block `crossReference` accepts — never the
 * reverse.
 */
export function extractionSources(funnel: Funnel): Block[] {
  return funnel.blocks.filter((block) => block.type === 'long_text' && acceptsExtraction(block))
}

/** The question a block asks, or null for a block that asks none. */
export const questionOf = (block: Block | undefined): string | null =>
  block !== undefined && 'question' in block ? block.question : null

/**
 * A field key that is legal on the first try, from the name an operator typed.
 *
 * `newFieldKey` in `editor/blocks.ts` is the precedent: a key is DERIVED rather
 * than asked for, and a collision takes a counter. The name stays in the
 * operator's words — it is what the lead drawer shows beside the fact — and the
 * key is the `lower_snake_case` identifier the rubric and the row both use.
 */
export function fieldKeyFor(name: string, taken: ReadonlySet<string>): string {
  const slug = name
    .normalize('NFKD')
    // NFKD splits "é" into "e" plus a combining mark, and \p{M} is every such mark.
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  const lead = slug.length === 0 ? 'field' : /^[a-z]/.test(slug) ? slug : `field_${slug}`
  const base = trimKey(lead, KEY_MAX)
  if (!taken.has(base)) return base
  for (let n = 2; n < 10_000; n += 1) {
    const suffix = `_${n}`
    const candidate = `${trimKey(base, KEY_MAX - suffix.length)}${suffix}`
    if (!taken.has(candidate)) return candidate
  }
  // Unreachable against a cap of fifty fields, and loud rather than a collision.
  throw new Error('no free extraction field key')
}

const trimKey = (key: string, max: number): string => key.slice(0, max).replace(/_+$/, '')

/** "access, planning, , access" → ["access", "planning"]: trimmed, with blanks and repeats dropped. */
export function parseValues(raw: string): string[] {
  const seen = new Set<string>()
  for (const part of raw.split(',')) {
    const value = part.trim()
    if (value.length > 0) seen.add(value)
  }
  return [...seen]
}

/** What the operator has typed so far, exactly as the inputs hold it. */
export type FieldDraft = {
  name: string
  description: string
  type: ExtractionType
  values: string
  lowest: string
  highest: string
  blockId: string
}

/** A new draft. With exactly one question to read from, it is already chosen. */
export function emptyDraft(funnel: Funnel): FieldDraft {
  const sources = extractionSources(funnel)
  return {
    name: '',
    description: '',
    type: 'string',
    values: '',
    lowest: '',
    highest: '',
    blockId: sources.length === 1 ? sources[0]!.id : '',
  }
}

export type FieldIssues = Partial<Record<'name' | 'description' | 'values' | 'range' | 'source', string>>

/**
 * The field this draft describes, or what to fix first.
 *
 * Every message says what to DO, never what went wrong (CLAUDE.md: errors carry
 * a fix). The schema parse at the end is the backstop, not the validation: if it
 * ever refuses what the checks above let through, the card adds nothing rather
 * than writing a rubric the save will refuse.
 *
 * A range or a set of values travels only with the type that takes one. A
 * Number draft switched to Text keeps its typed bounds in the inputs, and none of
 * them reach the rubric.
 */
export function buildField(
  draft: FieldDraft,
  funnel: Funnel,
): { ok: true; field: ExtractionField } | { ok: false; issues: FieldIssues } {
  const issues: FieldIssues = {}

  const name = draft.name.trim()
  if (name.length === 0) issues.name = COMPOSED.needsName
  else if (name.length > NAME_MAX) issues.name = COMPOSED.nameTooLong(NAME_MAX)

  const description = draft.description.trim()
  if (description.length === 0) issues.description = COMPOSED.needsDescription
  else if (description.length > DESCRIPTION_MAX) {
    issues.description = COMPOSED.descriptionTooLong(DESCRIPTION_MAX)
  }

  if (!extractionSources(funnel).some((block) => block.id === draft.blockId)) {
    issues.source = COMPOSED.needsSource
  }

  let enumValues: string[] | undefined
  if (draft.type === 'enum') {
    enumValues = parseValues(draft.values)
    if (enumValues.length < 2) issues.values = COMPOSED.needsValues
  }

  let min: number | undefined
  let max: number | undefined
  if (draft.type === 'number') {
    const lowest = numberOrBlank(draft.lowest)
    const highest = numberOrBlank(draft.highest)
    if (lowest === null || highest === null) {
      issues.range = COMPOSED.needsNumbers
    } else {
      min = lowest
      max = highest
      if (min !== undefined && max !== undefined && min > max) issues.range = COMPOSED.rangeBackwards
    }
  }

  if (Object.keys(issues).length > 0) return { ok: false, issues }

  const candidate: ExtractionField = {
    field_key: fieldKeyFor(name, new Set(funnel.rubric.extraction.map((field) => field.field_key))),
    block_id: draft.blockId,
    label: name,
    type: draft.type,
    description,
    ...(enumValues !== undefined ? { enum_values: enumValues } : {}),
    ...(min !== undefined ? { min } : {}),
    ...(max !== undefined ? { max } : {}),
  }
  const parsed = extractionFieldSchema.safeParse(candidate)
  if (!parsed.success) {
    return { ok: false, issues: { name: parsed.error.issues[0]?.message ?? COMPOSED.needsName } }
  }
  return { ok: true, field: parsed.data }
}

/** A blank input is no bound; anything else must be a finite number. `null` means it is not one. */
function numberOrBlank(raw: string): number | undefined | null {
  const trimmed = raw.trim()
  if (trimmed.length === 0) return undefined
  const value = Number(trimmed)
  return Number.isFinite(value) ? value : null
}

/**
 * Adds a field — the vocabulary's `add_extraction_field` since Story 27.5, the
 * operation a model's proposal calls (RULE-019). It refuses what the next save
 * would: a full rubric, a key it already has, a question not in `ai_extraction`
 * mode — and a refused add changes nothing, so `edit()` records no step.
 */
export function addExtractionField(draft: Funnel, field: ExtractionField): void {
  applyOperation(draft, { op: 'add_extraction_field', ...field })
}

/** What reads a field: the criteria that score it, by label, and how many conditions test it. */
export function readersOf(funnel: Funnel, fieldKey: string): { criteria: string[]; rules: number } {
  const readsIt = (subject: Condition['subject']): boolean =>
    subject.kind === 'extracted' && subject.field_key === fieldKey
  return {
    criteria: funnel.rubric.criteria
      .filter((criterion) => criterion.source === 'extracted' && criterion.field_key === fieldKey)
      .map((criterion) => criterion.label),
    rules:
      funnel.rubric.disqualifiers.filter((gate) => readsIt(gate.when.subject)).length +
      funnel.logic.filter((rule) => readsIt(rule.when.subject)).length,
  }
}

/** "A", "A and B", "A, B and C". */
export function joinNames(names: string[]): string {
  if (names.length <= 2) return names.join(' and ')
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/**
 * Why a field cannot be removed yet, or null when it can.
 *
 * `crossReference` refuses a criterion or a condition that names a field which
 * does not exist, so removing a field something reads writes a funnel the next
 * autosave refuses. The refusal happens HERE, before the save, naming what reads
 * the field — in the prompt's own words when that is a single criterion.
 */
export function removalRefusal(funnel: Funnel, fieldKey: string): string | null {
  const { criteria, rules } = readersOf(funnel, fieldKey)
  if (criteria.length === 1) return PROMPTED.refusedByCriterion(criteria[0]!)
  if (criteria.length > 1) return COMPOSED.refusedByCriteria(joinNames(criteria))
  if (rules > 0) return COMPOSED.refusedByRule
  return null
}

/** Removes a field nothing reads. A field something reads stays exactly where it is. */
export function removeExtractionField(draft: Funnel, fieldKey: string): void {
  if (removalRefusal(draft, fieldKey) !== null) return
  draft.rubric.extraction = draft.rubric.extraction.filter((field) => field.field_key !== fieldKey)
}

/** The drawn constraint for a Number field — "0 – 40" — or null when there is none to draw. */
export function rangeText(field: ExtractionField): string | null {
  if (field.type !== 'number') return null
  const { min, max } = field
  if (min !== undefined && max !== undefined) return `${min} – ${max}`
  if (min !== undefined) return COMPOSED.orMore(min)
  if (max !== undefined) return COMPOSED.upTo(max)
  return null
}
